import React, { useState, useRef, useEffect } from 'react';

/**
 * Componente OptimizedImage
 * 
 * Renderiza imágenes optimizadas con:
 * - Soporte WebP con fallback a JPG/PNG
 * - Múltiples tamaños responsive (srcSet)
 * - Carga diferida (lazy loading)
 * - Placeholder mientras carga
 * - Manejo de errores
 * 
 * @param {string} src - Nombre base de la imagen (sin extensión ni sufijo de tamaño)
 * @param {string} alt - Texto alternativo (requerido para accesibilidad)
 * @param {string} className - Clases CSS adicionales
 * @param {string} fallbackSrc - URL de imagen de respaldo si falla la carga
 * @param {string} sizes - Atributo sizes para srcSet (default: responsive)
 * @param {boolean} priority - Si es true, no usa lazy loading (para imágenes above the fold)
 * @param {string} objectFit - Ajuste de la imagen: cover, contain, fill, etc.
 * @param {function} onLoad - Callback cuando la imagen carga
 * @param {function} onError - Callback cuando hay error
 */
const OptimizedImage = ({
  src,
  alt,
  className = '',
  fallbackSrc,
  sizes = '(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw',
  priority = false,
  objectFit = 'cover',
  onLoad,
  onError,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef(null);

  // Determinar si es una URL externa o imagen local
  const isExternalUrl = src?.startsWith('http') || src?.startsWith('data:');
  
  // Generar rutas de imágenes
  const getImagePaths = () => {
    if (isExternalUrl) {
      return {
        webp: null,
        fallback: src,
        srcSet: null
      };
    }

    // Limpiar el nombre base
    const baseName = src?.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '').toLowerCase();
    const basePath = `/images/${baseName}`;

    return {
      webp: {
        srcSet: `${basePath}-640w.webp 640w, ${basePath}-1280w.webp 1280w, ${basePath}-1920w.webp 1920w`,
        src: `${basePath}-1280w.webp`
      },
      fallback: {
        srcSet: `${basePath}-640w.jpg 640w, ${basePath}-1280w.jpg 1280w, ${basePath}-1920w.jpg 1920w`,
        src: `${basePath}-1920w.jpg`
      }
    };
  };

  const paths = getImagePaths();

  // Intersection Observer para lazy loading
  useEffect(() => {
    if (priority || isInView) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px 0px', // Cargar un poco antes de que entre en viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority, isInView]);

  const handleLoad = (e) => {
    setIsLoaded(true);
    onLoad?.(e);
  };

  const handleError = (e) => {
    setHasError(true);
    onError?.(e);
  };

  // Estilos base
  const containerStyles = {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#f3f4f6', // gray-100 como placeholder
  };

  const imageStyles = {
    objectFit,
    transition: 'opacity 0.3s ease-in-out',
    opacity: isLoaded ? 1 : 0,
  };

  const placeholderStyles = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    transition: 'opacity 0.3s ease-in-out',
    opacity: isLoaded ? 0 : 1,
    pointerEvents: 'none',
  };

  // Si hay error, mostrar fallback o placeholder
  if (hasError) {
    if (fallbackSrc) {
      return (
        <img
          src={fallbackSrc}
          alt={alt}
          className={className}
          style={{ objectFit }}
          {...props}
        />
      );
    }
    
    return (
      <div 
        className={className}
        style={{ ...containerStyles, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        {...props}
      >
        <svg 
          className="w-12 h-12 text-gray-300" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={1.5} 
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
          />
        </svg>
      </div>
    );
  }

  // Para URLs externas o data URLs, usar img simple
  if (isExternalUrl) {
    return (
      <div ref={imgRef} className={className} style={containerStyles} {...props}>
        {/* Placeholder */}
        <div style={placeholderStyles}>
          <div className="animate-pulse w-full h-full bg-gray-200" />
        </div>
        
        {isInView && (
          <img
            src={src}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
            style={{ ...imageStyles, width: '100%', height: '100%' }}
            className="w-full h-full"
          />
        )}
      </div>
    );
  }

  // Para imágenes locales optimizadas, usar picture con WebP
  return (
    <div ref={imgRef} className={className} style={containerStyles} {...props}>
      {/* Placeholder animado */}
      <div style={placeholderStyles}>
        <div className="animate-pulse w-full h-full bg-gray-200" />
      </div>
      
      {isInView && (
        <picture>
          {/* WebP source (navegadores modernos) */}
          {paths.webp && (
            <source
              type="image/webp"
              srcSet={paths.webp.srcSet}
              sizes={sizes}
            />
          )}
          
          {/* Fallback JPG/PNG (navegadores antiguos) */}
          <img
            src={paths.fallback?.src || paths.fallback}
            srcSet={paths.fallback?.srcSet}
            sizes={sizes}
            alt={alt}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
            style={{ ...imageStyles, width: '100%', height: '100%' }}
            className="w-full h-full"
          />
        </picture>
      )}
    </div>
  );
};

/**
 * Componente para imágenes de fondo optimizadas
 */
export const OptimizedBackgroundImage = ({
  src,
  children,
  className = '',
  overlayColor = 'rgba(0, 0, 0, 0.5)',
  ...props
}) => {
  const isExternalUrl = src?.startsWith('http') || src?.startsWith('data:');
  const baseName = src?.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '').toLowerCase();
  
  const backgroundImage = isExternalUrl 
    ? src 
    : `/images/${baseName}-1920w.webp`;

  return (
    <div
      className={`relative bg-cover bg-center ${className}`}
      style={{
        backgroundImage: `url(${backgroundImage})`,
      }}
      {...props}
    >
      {/* Overlay */}
      <div 
        className="absolute inset-0" 
        style={{ backgroundColor: overlayColor }}
      />
      
      {/* Contenido */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
};

/**
 * Hook para precargar imágenes
 */
export const usePreloadImage = (src) => {
  useEffect(() => {
    if (!src) return;
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src.startsWith('http') ? src : `/images/${src}-1280w.webp`;
    document.head.appendChild(link);
    
    return () => {
      document.head.removeChild(link);
    };
  }, [src]);
};

export default OptimizedImage;
